import { ButtonFlavor, ButtonSize, ButtonType, ExButton } from '@boomi/exosphere';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { type AutomateError, deleteReadNotifications, markNotificationsAsRead } from '../api';
import { useApp } from '../common/AppProvider';
import ItemList from '../common/ItemList';
import { translations } from '../translations/translations';
import type { Notification } from '../types/types';
import NotificationItem from './NotificationItem';

import './notifications.css';

type TaskFilterOption = { label: string | undefined; value: string | undefined };

const Notifications = () => {
    const { language, tasks, notifications, setNotifications, alertError, setIsLoading } = useApp();

    const [selectedTask, setSelectedTask] = useState<TaskFilterOption | null>(null);
    const [displayNotifications, setDisplayNotifications] = useState<Notification[]>(notifications);

    const [searchParams] = useSearchParams();

    const sortByDate = (notifications: Notification[]) => {
        // The sort function does not create a new array, it mutates the original.
        // Since it's still the same array, React thinks the state hasn't changed
        // and skips re-rendering, so we make a new array with and sort it.
        return [...notifications].sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);

            return dateB.valueOf() - dateA.valueOf();
        });
    };

    useEffect(() => {
        const taskId = searchParams.get('taskId');
        if (taskId) {
            const task = tasks.find((t) => t.id === taskId);
            if (task) {
                setSelectedTask({ value: taskId, label: task.displayName });
            }
        }
    }, [searchParams, tasks]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (selectedTask) {
            const filteredNotifications = notifications.filter(
                (notification) => notification.taskId === selectedTask.value,
            );
            setDisplayNotifications(sortByDate(filteredNotifications));
        } else {
            setDisplayNotifications(sortByDate(notifications));
        }
    }, [selectedTask, notifications]);

    const getUnreadNotifications = () => {
        const unreadNotificationIds: string[] = [];
        notifications.forEach((element) => {
            if (!element.read) {
                unreadNotificationIds.push(element.id);
            }
        });

        return unreadNotificationIds;
    };

    const readAllNotifications = async () => {
        const unreadNotificationIds: string[] = getUnreadNotifications();
        if (unreadNotificationIds.length === 0) {
            return;
        }

        setIsLoading(true);

        await markNotificationsAsRead(unreadNotificationIds)
            .then(() => {
                notifications.forEach((_, index) => {
                    notifications[index].read = true;
                });
                setNotifications([...notifications]);
            })
            .catch((e: AutomateError) =>
                alertError({
                    message: translations[language].ERROR_reading_all_notifications,
                    traceId: e.json?.traceId,
                }),
            )
            .finally(() => setIsLoading(false));
    };

    const deleteAllReadNotifications = async () => {
        setIsLoading(true);

        await deleteReadNotifications()
            .then(() =>
                setNotifications(
                    notifications.filter((element) => {
                        return !element.read;
                    }),
                ),
            )
            .catch((e: AutomateError) =>
                alertError({
                    message: translations[language].ERROR_deleting_read_notifications,
                    traceId: e.json?.traceId,
                }),
            )
            .finally(() => setIsLoading(false));
    };

    const getFilterOptions = () => {
        // We only need the notifications that are linked with tasks and no duplicates. Also filter out the current selected task.
        const filteredNotifications = notifications.filter(
            (notification, index, self) =>
                notification.taskId !== undefined &&
                index === self.findIndex((t) => t.taskId === notification.taskId) &&
                notification.taskId !== selectedTask?.value,
        );

        return filteredNotifications.map((notification) => {
            return {
                label: tasks.find((task) => task.id === notification.taskId)?.displayName,
                value: notification.taskId,
            };
        });
    };

    return (
        <ItemList
            title={translations[language].USER_NOTIFICATIONS_heading}
            toolbarFilters={
                <Select
                    className="react-select-container"
                    classNamePrefix="react-select"
                    options={getFilterOptions()}
                    onChange={setSelectedTask}
                    placeholder={translations[language].USER_NOTIFICATIONS_select_placeholder}
                    value={selectedTask}
                    isMulti={false}
                    isClearable={true}
                    menuPlacement="auto"
                />
            }
            toolbarActions={
                <>
                    <ExButton
                        className="exo-button"
                        data-testId="confirmCancelButton"
                        flavor={ButtonFlavor.BASE}
                        size={ButtonSize.LARGE}
                        type={ButtonType.SECONDARY}
                        disabled={getUnreadNotifications().length === 0}
                        onClick={readAllNotifications}
                    >
                        {translations[language].USER_NOTIFICATIONS_all_read}
                    </ExButton>

                    <ExButton
                        className="exo-button"
                        data-testId="confirmDeleteRead"
                        flavor={ButtonFlavor.RISKY}
                        size={ButtonSize.LARGE}
                        type={ButtonType.PRIMARY}
                        disabled={notifications.length === 0}
                        onClick={deleteAllReadNotifications}
                    >
                        {translations[language].USER_NOTIFICATIONS_delete_read}
                    </ExButton>
                </>
            }
            items={displayNotifications}
            itemContent={(notification) => (
                <NotificationItem
                    key={notification.id}
                    notification={notification}
                    task={tasks.find((task) => task.id === notification.taskId)}
                />
            )}
        />
    );
};

export default Notifications;
